import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Search, ChevronDown } from "lucide-react";
import Seo from "../components/Seo";
import { CTA } from "../components/sections";
import { api } from "../lib/api";
import { MELB_IMG, OG_IMG } from "../data/site";

export default function Areas() {
  const [suburbs, setSuburbs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get("/content/suburbs").then((r) => setSuburbs(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => suburbs.filter((s) => s.active !== false && s.name.toLowerCase().includes(q.toLowerCase())), [suburbs, q]);
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((s) => { (g[s.region || "Other"] = g[s.region || "Other"] || []).push(s); });
    Object.values(g).forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
    return g;
  }, [filtered]);

  const regions = Object.keys(grouped).sort();
  const priority = suburbs.filter((s) => s.priority).sort((a, b) => a.name.localeCompare(b.name));

  const jsonLd = {
    "@context": "https://schema.org", "@type": "AutoDealer", name: "Car To Cash",
    telephone: "+61431206206",
    areaServed: { "@type": "GeoCircle", geoMidpoint: { "@type": "GeoCoordinates", latitude: -37.8136, longitude: 144.9631 }, geoRadius: "150000" },
  };

  return (
    <>
      <Seo title="Areas We Service | Cash for Cars Across Melbourne & Victoria | Car To Cash" description="We buy cars for cash across every Melbourne suburb and every town within roughly 150km of the CBD. Search your suburb — free valuation, payment on the spot and free removal." canonical="https://cartocash.com.au/areas" image={OG_IMG} jsonLd={jsonLd} />

      <section className="relative noise-dark text-white">
        <div className="absolute inset-0 opacity-25"><img src={MELB_IMG} alt="Melbourne skyline" className="h-full w-full object-cover" /></div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <h1 className="font-display text-4xl sm:text-5xl font-black">Areas we service</h1>
          <p className="mt-3 max-w-xl text-white/80 text-lg">Every Melbourne suburb and every town within roughly 150km of the CBD. We come to you — free valuation, free removal, cash on the spot.</p>
        </div>
      </section>

      {/* Priority centres */}
      {priority.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 py-14">
          <h2 className="font-display text-2xl font-extrabold text-brand-navy">Popular locations</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="priority-grid">
            {priority.map((s) => (
              <Link key={s.id} to={`/areas/${s.slug}`} className="group rounded-2xl border border-brand-line bg-white p-5 hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`suburb-card-${s.slug}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg font-extrabold text-brand-navy flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-green" /> {s.name}</h3>
                  <ArrowRight className="h-5 w-5 text-brand-green group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="mt-1 text-sm text-brand-muted">{s.pickup_window ? `Pickup ${s.pickup_window}` : "Free pickup & valuation"}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Full coverage, grouped + searchable */}
      <section className="bg-brand-surface">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <h2 className="font-display text-2xl font-extrabold text-brand-navy">Full coverage list</h2>
          <div className="mt-4 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your suburb or town..." className="w-full rounded-full border-2 border-brand-line bg-white pl-11 pr-4 py-3 outline-none focus:border-brand-gold" data-testid="suburb-search" />
          </div>

          <div className="mt-6 space-y-3" data-testid="region-list">
            {regions.map((region) => {
              const isOpen = q ? true : open[region];
              return (
                <div key={region} className="rounded-2xl border border-brand-line bg-white overflow-hidden">
                  <button onClick={() => setOpen((o) => ({ ...o, [region]: !o[region] }))} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`region-toggle-${region}`}>
                    <span className="font-display font-extrabold text-brand-navy">{region} <span className="text-brand-muted font-sans font-semibold">({grouped[region].length})</span></span>
                    <ChevronDown className={`h-5 w-5 text-brand-navy transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 flex flex-wrap gap-x-4 gap-y-2">
                      {grouped[region].map((s) => (
                        s.has_page
                          ? <Link key={s.id} to={`/areas/${s.slug}`} className="text-sm font-semibold text-brand-navy hover:text-brand-green underline underline-offset-2" data-testid={`area-link-${s.slug}`}>{s.name}</Link>
                          : <span key={s.id} className="text-sm text-brand-muted" data-testid={`area-text-${s.slug}`}>{s.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {regions.length === 0 && <p className="text-brand-muted">No suburbs match "{q}". <a href="tel:0431206206" className="font-bold text-brand-navy underline">Call us</a> to check your area.</p>}
          </div>
          <p className="mt-6 text-sm text-brand-muted">Don't see your suburb? We travel widely — <a href="tel:0431206206" className="font-bold text-brand-navy underline">call 0431 206 206</a> to confirm coverage.</p>
        </div>
      </section>
      <CTA />
    </>
  );
}
